import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Save, User, Building2, Sparkles, CheckCircle2, AlertCircle, Bell, Receipt, Download, BellRing, Crown } from "lucide-react";
import TwoFAPanel from "../components/TwoFAPanel";
import AvatarUploader from "../components/AvatarUploader";
import { pushSupported, pushPermission, getCurrentSubscription, enablePush, disablePush, sendTestPush } from "../lib/push";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KI_LEVELS = ["Einsteiger", "Fortgeschritten", "Profi", "Dienstleister"];

const NOTIFICATION_OPTIONS = [
  { key: "all", label: "Alle Benachrichtigungen", hint: "Master-Schalter — wenn aus, sendet [KI]NPUT keine Info-Mails mehr." },
  { key: "board_posts", label: "Neue Board-Einträge", hint: "Wenn ein Mitglied einen neuen Post-It (Suche/Biete Hilfe etc.) anlegt." },
  { key: "events", label: "Neue Events", hint: "Neue Workshops, Vorträge, Meetups." },
  { key: "learning", label: "Neues Lernmaterial", hint: "Neue KI-Lernen Tracks & Kapitel." },
  { key: "tools", label: "Neue AI Tools", hint: "Neu aufgenommene Tools im Verzeichnis." },
  { key: "chat_messages", label: "Neue Chat-Nachrichten", hint: "Off-Platform E-Mail bei neuer DM (kann laut werden)." },
  { key: "new_members", label: "Neue Mitglieder", hint: "Wenn ein neues Community-Mitglied freigeschaltet wird." },
];

const DEFAULT_PREFS = { all: true, board_posts: true, events: true, learning: true, tools: true, chat_messages: false, new_members: true };

function SaveButtonLabel({ isSaving, saved }) {
  if (isSaving) return "Wird gespeichert...";
  if (saved) return "Gespeichert!";
  return "Profil speichern";
}

function Section({ icon: Icon, label, number, title, children }) {
  return (
    <div className="relative bg-gradient-to-br from-white/[0.015] to-transparent border border-white/[0.07] p-6 sm:p-7">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2.5">
          <Icon size={16} className="text-[#00e5ff]/80" strokeWidth={1.6} />
          <span className="text-[11px] uppercase tracking-[0.28em] text-[#00e5ff]/80 font-outfit font-semibold">{label}</span>
        </div>
        <span className="text-[11px] font-outfit tracking-widest text-white/15">{number}</span>
      </div>
      <h2 className="font-outfit font-bold text-xl text-white tracking-tight mb-5">{title}</h2>
      {children}
    </div>
  );
}

function FieldLabel({ children }) {
  return <Label className="text-white/50 text-[11px] uppercase tracking-[0.2em] font-outfit font-medium">{children}</Label>;
}

const inputCls = "mt-2 bg-black border-white/10 text-white rounded-none h-11 text-[15px] focus:border-[#00e5ff]/60 focus:ring-0 transition-colors";

export default function ProfilePage() {
  const { refreshUser } = useAuth();
  const location = useLocation();
  const showGateBanner = location.state?.profileGate === true;
  const showTwoFaBanner = location.state?.twoFaGate === true;
  const [profile, setProfile] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [invoices, setInvoices] = useState([]);

  const fetchProfile = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/profile`, { withCredentials: true });
      setProfile(data);
      setError("");
    } catch (err) {
      console.error("Failed to fetch profile:", err);
      setError("Profil konnte nicht geladen werden.");
    }
  }, []);

  const fetchInvoices = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/payments/invoices`, { withCredentials: true });
      setInvoices(data || []);
    } catch (err) {
      console.error("Failed to fetch invoices:", err);
    }
  }, []);

  useEffect(() => { fetchProfile(); fetchInvoices(); }, [fetchProfile, fetchInvoices]);

  // Donation callback — Stripe returns with ?donation_session=cs_live_...
  const [donationStatus, setDonationStatus] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sid = params.get("donation_session");
    const cancelled = params.get("donation_cancelled");
    if (cancelled) {
      setDonationStatus({ type: "cancelled", msg: "Spende abgebrochen — kein Betrag abgebucht." });
      window.history.replaceState({}, "", location.pathname);
      return;
    }
    if (!sid) return;
    let cancelledFlag = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelledFlag) return;
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/donations/status/${sid}`, { withCredentials: true });
        if (data.payment_status === "paid") {
          setDonationStatus({
            type: "success",
            msg: `Vielen Dank! Deine Spende von ${Number(data.amount).toFixed(2).replace(".", ",")} € wurde verbucht. Die Rechnung findest du unten.`,
          });
          fetchInvoices();
          window.history.replaceState({}, "", location.pathname);
          return;
        }
        if (attempts < 8) setTimeout(poll, 1500);
        else {
          setDonationStatus({ type: "pending", msg: "Spende wird noch verarbeitet — bitte Seite gleich neu laden." });
          window.history.replaceState({}, "", location.pathname);
        }
      } catch {
        setDonationStatus({ type: "error", msg: "Status konnte nicht abgefragt werden." });
        window.history.replaceState({}, "", location.pathname);
      }
    };
    poll();
    return () => { cancelledFlag = true; };
  }, [location.pathname, location.search, fetchInvoices]);

  // Push subscription state
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushMsg, setPushMsg] = useState("");
  const pushSup = typeof window !== "undefined" ? pushSupported() : false;
  const pushPerm = typeof window !== "undefined" ? pushPermission() : "unsupported";
  useEffect(() => {
    if (!pushSup) return;
    getCurrentSubscription().then((s) => setPushEnabled(!!s));
  }, [pushSup]);

  const togglePush = async () => {
    setPushBusy(true); setPushMsg("");
    try {
      if (pushEnabled) { await disablePush(); setPushEnabled(false); setPushMsg("Push-Benachrichtigungen deaktiviert."); }
      else { await enablePush(); setPushEnabled(true); setPushMsg("Aktiviert — hier klicken für Test-Push →"); }
    } catch (e) { setPushMsg(e?.message || "Fehler"); }
    finally { setPushBusy(false); }
  };

  const handleTestPush = async () => {
    try { await sendTestPush(); setPushMsg("Test-Push gesendet — schau auf dein Gerät!"); }
    catch (e) { setPushMsg(e?.response?.data?.detail || "Test fehlgeschlagen"); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setSaved(false);
    setError("");
    try {
      const { data } = await axios.put(`${API}/profile`, {
        first_name: profile.first_name,
        last_name: profile.last_name,
        phone: profile.phone,
        company: profile.company,
        position: profile.position,
        branche: profile.branche,
        street: profile.street,
        zip: profile.zip,
        city: profile.city,
        country: profile.country,
        ki_interests: profile.ki_interests,
        ki_level: profile.ki_level,
        motto: profile.motto,
        notification_prefs: profile.notification_prefs || DEFAULT_PREFS,
      }, { withCredentials: true });
      setProfile(data);
      setSaved(true);
      refreshUser();
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error("Failed to save profile:", err);
      setError("Profil konnte nicht gespeichert werden.");
    } finally {
      setIsSaving(false);
    }
  };

  const update = (field, value) => setProfile((prev) => ({ ...prev, [field]: value }));

  if (!profile) {
    return (
      <div className="min-h-screen bg-black">
        <Navbar />
        <div className="flex items-center justify-center py-24">
          <div className="w-8 h-8 border-2 border-[#00e5ff]/60 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white antialiased selection:bg-[#00e5ff]/30 relative overflow-x-hidden">
      {/* Ambient Background */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 right-0 w-[45%] h-[55%] rounded-full blur-[100px]" style={{ background: 'radial-gradient(circle, rgba(0,229,255,0.06) 0%, transparent 70%)' }} />
        <div className="absolute bottom-0 left-0 w-[45%] h-[55%] rounded-full blur-[100px]" style={{ background: 'radial-gradient(circle, rgba(120,0,255,0.05) 0%, transparent 70%)' }} />
      </div>

      <div className="relative z-10">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
          {/* Hero */}
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-3">
              <span className="w-8 h-px bg-[#00e5ff]" />
              <span className="font-outfit text-[11px] uppercase tracking-[0.32em] text-[#00e5ff]/80 font-semibold">
                Dein Profil
              </span>
            </div>
            <h1 className="font-outfit font-black text-4xl sm:text-5xl tracking-tighter leading-[0.95] text-white mb-3" data-testid="profile-heading">
              Mein <span className="text-[#00e5ff]">Profil</span>
            </h1>
            <p className="text-base sm:text-lg text-white/50 max-w-lg leading-relaxed">
              Bearbeite deine Daten und KI-Interessen — so findet dich die Community leichter.
            </p>
            {profile?.founding_member && (
              <div className="mt-5 inline-flex items-center gap-3 pl-3 pr-4 py-2.5 bg-gradient-to-r from-amber-300/15 via-amber-400/10 to-amber-300/15 border border-amber-300/50" data-testid="profile-founding-badge">
                <Crown size={18} className="text-amber-300 fill-amber-300/30" strokeWidth={2.2} />
                <div>
                  <div className="text-[10.5px] uppercase tracking-[0.28em] text-amber-300/80 font-outfit font-semibold leading-none mb-0.5">
                    Gründungsmitglied
                  </div>
                  <div className="text-[13px] text-amber-100 font-outfit font-bold tracking-tight leading-none">
                    #{String(profile.founding_number || 0).padStart(3, "0")} · eines der ersten 50 Mitglieder
                  </div>
                </div>
              </div>
            )}
          </div>

          {(showGateBanner || profile?.profile_complete === false) && (
            <div
              className="mb-6 border-l-4 border-[#00e5ff] bg-[#00e5ff]/[0.06] p-5 flex items-start gap-3"
              data-testid="profile-gate-banner"
            >
              <AlertCircle size={20} className="text-[#00e5ff] shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-outfit font-semibold text-white text-base mb-1">Profil vervollständigen erforderlich</p>
                <p className="text-white/70 text-sm leading-relaxed">
                  Um Beiträge zu schreiben, den Chat zu nutzen oder dich für Events anzumelden, fülle bitte alle
                  Pflichtfelder aus: <span className="text-[#00e5ff]">Name, Profilfoto, Unternehmen, Position, Branche, Geschäftsadresse, KI-Level und KI-Interessen</span>.
                  So bleibt [KI]NPUT ein echter Austausch-Raum für Unternehmer — keine anonymen Touristen.
                </p>
              </div>
            </div>
          )}

          {donationStatus && (
            <div
              data-testid="donation-status-banner"
              className={`mb-6 border-l-4 p-5 flex items-start gap-3 ${
                donationStatus.type === "success"
                  ? "border-[#00e5ff] bg-[#00e5ff]/[0.06]"
                  : donationStatus.type === "cancelled"
                    ? "border-white/30 bg-white/[0.03]"
                    : donationStatus.type === "pending"
                      ? "border-yellow-400 bg-yellow-400/[0.06]"
                      : "border-red-400 bg-red-400/[0.06]"
              }`}
            >
              <AlertCircle
                size={20}
                className={`shrink-0 mt-0.5 ${
                  donationStatus.type === "success" ? "text-[#00e5ff]"
                    : donationStatus.type === "cancelled" ? "text-white/60"
                      : donationStatus.type === "pending" ? "text-yellow-400"
                        : "text-red-400"
                }`}
              />
              <div className="flex-1">
                <p className="font-outfit font-semibold text-white text-base mb-1">
                  {donationStatus.type === "success" ? "Spende erfolgreich"
                    : donationStatus.type === "cancelled" ? "Spende abgebrochen"
                      : donationStatus.type === "pending" ? "Spende wird verarbeitet"
                        : "Fehler"}
                </p>
                <p className="text-white/70 text-sm leading-relaxed">{donationStatus.msg}</p>
              </div>
              <button
                onClick={() => setDonationStatus(null)}
                data-testid="donation-status-dismiss"
                className="text-white/40 hover:text-white transition-colors"
                aria-label="Schließen"
              >×</button>
            </div>
          )}

          {showTwoFaBanner && (
            <div
              className="mb-6 border-l-4 border-yellow-400 bg-yellow-400/[0.06] p-5 flex items-start gap-3"
              data-testid="twofa-gate-banner"
            >
              <AlertCircle size={20} className="text-yellow-400 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-outfit font-semibold text-white text-base mb-1">2FA-Aktivierung erforderlich</p>
                <p className="text-white/70 text-sm leading-relaxed">
                  Als Administrator musst du Zwei-Faktor-Authentifizierung einrichten, bevor du weiter im
                  Admin-Bereich arbeiten kannst. Scrolle zum Bereich „Sicherheit · 2FA" unten und scanne den QR-Code
                  mit einer Authenticator-App (Google Authenticator, Authy, 1Password, ...).
                </p>
              </div>
            </div>
          )}

          <form onSubmit={handleSave} className="space-y-5" data-testid="profile-form">
            <Section icon={User} label="Identität" number="01" title="Persönliche Daten">
              <div className="mb-6 pb-6 border-b border-white/[0.06]">
                <AvatarUploader
                  currentUrl={profile.avatar_url || ""}
                  name={`${profile.first_name || ""} ${profile.last_name || ""}`}
                  onChange={(url) => { update("avatar_url", url); refreshUser?.(); }}
                />
              </div>              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <FieldLabel>Vorname *</FieldLabel>
                  <Input required value={profile.first_name || ""} onChange={(e) => update("first_name", e.target.value)} data-testid="profile-firstname" className={inputCls} />
                </div>
                <div>
                  <FieldLabel>Nachname *</FieldLabel>
                  <Input required value={profile.last_name || ""} onChange={(e) => update("last_name", e.target.value)} data-testid="profile-lastname" className={inputCls} />
                </div>
              </div>
              <div className="mt-4">
                <FieldLabel>E-Mail (nicht änderbar)</FieldLabel>
                <Input value={profile.email || ""} disabled data-testid="profile-email" className="mt-2 bg-black/50 border-white/[0.06] text-white/40 rounded-none h-11 text-[15px] cursor-not-allowed" />
              </div>
            </Section>

            <Section icon={Building2} label="Business" number="02" title="Unternehmen">
              <div className="space-y-4">
                <div>
                  <FieldLabel>Unternehmensname *</FieldLabel>
                  <Input required value={profile.company || ""} onChange={(e) => update("company", e.target.value)} data-testid="profile-company" className={inputCls} placeholder="z.B. Muster GmbH" />
                </div>
                <div>
                  <FieldLabel>Position / Rolle *</FieldLabel>
                  <Input required value={profile.position || ""} onChange={(e) => update("position", e.target.value)} data-testid="profile-position" className={inputCls} placeholder="z.B. Geschäftsführer" />
                </div>
                <div>
                  <FieldLabel>Branche *</FieldLabel>
                  <Input required value={profile.branche || ""} onChange={(e) => update("branche", e.target.value)} data-testid="profile-branche" className={inputCls} placeholder="z.B. Maschinenbau, IT, Marketing" />
                </div>
                <div>
                  <FieldLabel>Telefonnummer</FieldLabel>
                  <Input value={profile.phone || ""} onChange={(e) => update("phone", e.target.value)} data-testid="profile-phone" className={inputCls} placeholder="+49 123 456789" />
                </div>
                <div className="pt-2 mt-2 border-t border-white/[0.05]">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-outfit font-semibold mb-3">Geschäftsadresse</p>
                  <div className="space-y-3">
                    <div>
                      <FieldLabel>Straße & Hausnummer *</FieldLabel>
                      <Input required value={profile.street || ""} onChange={(e) => update("street", e.target.value)} data-testid="profile-street" className={inputCls} placeholder="Hauptstraße 12" />
                    </div>
                    <div className="grid grid-cols-[120px_1fr] gap-3">
                      <div>
                        <FieldLabel>PLZ *</FieldLabel>
                        <Input required value={profile.zip || ""} onChange={(e) => update("zip", e.target.value)} data-testid="profile-zip" className={inputCls} placeholder="89257" />
                      </div>
                      <div>
                        <FieldLabel>Ort *</FieldLabel>
                        <Input required value={profile.city || ""} onChange={(e) => update("city", e.target.value)} data-testid="profile-city" className={inputCls} placeholder="Illertissen" />
                      </div>
                    </div>
                    <div>
                      <FieldLabel>Land</FieldLabel>
                      <Input value={profile.country || ""} onChange={(e) => update("country", e.target.value)} data-testid="profile-country" className={inputCls} placeholder="Deutschland" />
                    </div>
                  </div>
                </div>
              </div>
            </Section>

            <Section icon={Sparkles} label="KI-Profil" number="03" title="Deine KI-Reise">
              <div className="space-y-4">
                <div>
                  <FieldLabel>KI-Erfahrungslevel *</FieldLabel>
                  <Select value={profile.ki_level || ""} onValueChange={(v) => update("ki_level", v)}>
                    <SelectTrigger className={inputCls} data-testid="profile-ki-level">
                      <SelectValue placeholder="Bitte wählen" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border-white/10 text-white rounded-none">
                      {KI_LEVELS.map((level) => (
                        <SelectItem key={level} value={level} className="hover:bg-white/5 focus:bg-white/5 focus:text-white rounded-none text-[14px]">{level}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <FieldLabel>KI-Interessen *</FieldLabel>
                  <Textarea required value={profile.ki_interests || ""} onChange={(e) => update("ki_interests", e.target.value)} data-testid="profile-ki-interests" className="mt-2 bg-black border-white/10 text-white rounded-none focus:border-[#00e5ff]/60 focus:ring-0 min-h-[110px] text-[15px]" placeholder="z.B. Automatisierung, Bildgenerierung, Chatbots" />
                </div>
                <div>
                  <FieldLabel>Dein Motto (optional)</FieldLabel>
                  <Input
                    value={profile.motto || ""}
                    onChange={(e) => update("motto", e.target.value.slice(0, 120))}
                    data-testid="profile-motto"
                    className={inputCls}
                    placeholder="z.B. „Mach, bevor du alles verstehst."
                    maxLength={120}
                  />
                  <p className="text-[11px] text-white/30 mt-1">{(profile.motto || "").length} / 120 · erscheint bei Mitgliedern</p>
                </div>
              </div>
            </Section>

            <Section icon={Bell} label="Benachrichtigungen" number="04" title="E-Mail-Einstellungen">
              <p className="text-[13px] text-white/45 mb-4 -mt-2">Entscheide selbst, worüber [KI]NPUT dich per E-Mail informiert. Der Master-Schalter „Alle" überschreibt alles.</p>
              <div className="space-y-1">
                {NOTIFICATION_OPTIONS.map((opt) => {
                  const prefs = profile.notification_prefs || DEFAULT_PREFS;
                  const checked = prefs[opt.key] ?? DEFAULT_PREFS[opt.key];
                  const masterOff = prefs.all === false && opt.key !== "all";
                  return (
                    <label key={opt.key} data-testid={`notif-${opt.key}`} className={`flex items-start gap-3 py-3 px-3 border border-white/[0.05] cursor-pointer transition-colors ${masterOff ? "opacity-40" : "hover:bg-white/[0.025]"} ${opt.key === "all" ? "bg-[#00e5ff]/[0.03] border-[#00e5ff]/20" : ""}`}>
                      <input
                        type="checkbox"
                        checked={!!checked}
                        disabled={masterOff}
                        onChange={(e) => update("notification_prefs", { ...prefs, [opt.key]: e.target.checked })}
                        className="mt-1 w-4 h-4 accent-[#00e5ff] cursor-pointer"
                      />
                      <div className="flex-1 min-w-0">
                        <div className={`text-[14px] font-outfit font-semibold ${opt.key === "all" ? "text-[#00e5ff]" : "text-white"}`}>{opt.label}</div>
                        <p className="text-[12px] text-white/45 mt-0.5 leading-snug">{opt.hint}</p>
                      </div>
                    </label>
                  );
                })}
              </div>

              {/* Web-Push */}
              <div className="mt-5 pt-5 border-t border-white/[0.06]" data-testid="push-section">
                <div className="flex items-center justify-between gap-4 mb-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <BellRing size={14} className="text-[#00e5ff]" />
                      <span className="text-[13px] font-outfit font-semibold text-white tracking-tight">Push-Benachrichtigungen (Browser)</span>
                    </div>
                    <p className="text-[12px] text-white/45 mt-1 leading-snug">Instant-Push auf diesem Gerät — z. B. wenn jemand auf deinen Forum-Thread antwortet.</p>
                  </div>
                  {pushSup ? (
                    <button type="button" onClick={togglePush} disabled={pushBusy || pushPerm === "denied"} data-testid="push-toggle-btn"
                      className={`h-9 px-4 text-[12px] uppercase tracking-[0.18em] font-outfit font-bold border rounded-none transition-colors shrink-0 ${
                        pushEnabled
                          ? "bg-[#2ecc71]/10 border-[#2ecc71]/50 text-[#2ecc71] hover:bg-[#2ecc71]/15"
                          : pushPerm === "denied"
                          ? "border-red-400/40 text-red-400/60 cursor-not-allowed"
                          : "bg-[#00e5ff]/10 border-[#00e5ff]/50 text-[#00e5ff] hover:bg-[#00e5ff]/20"
                      }`}>
                      {pushBusy ? "..." : pushEnabled ? "Aktiv ✓" : pushPerm === "denied" ? "Blockiert im Browser" : "Aktivieren"}
                    </button>
                  ) : (
                    <span className="text-[11px] text-white/35 font-outfit shrink-0">Browser unterstützt keine Push</span>
                  )}
                </div>
                {pushMsg && (
                  <div className="mt-2 text-[12px] text-white/60 flex items-center gap-2">
                    <span>{pushMsg}</span>
                    {pushEnabled && <button type="button" onClick={handleTestPush} data-testid="push-test-btn" className="text-[#00e5ff] hover:underline font-semibold">Test-Push senden</button>}
                  </div>
                )}
              </div>
            </Section>

            <Button type="submit" disabled={isSaving} data-testid="profile-save-button" className="w-full bg-[#00e5ff] text-black hover:bg-[#00e5ff] hover:shadow-[0_0_40px_-4px_rgba(0,229,255,0.7)] rounded-none font-outfit font-semibold h-12 text-[15px] tracking-wide transition-all duration-300 hover:-translate-y-0.5">
              {saved ? <CheckCircle2 size={16} className="mr-2" /> : <Save size={16} className="mr-2" />}
              <SaveButtonLabel isSaving={isSaving} saved={saved} />
            </Button>
          </form>

          <div className="mt-6">
            <Section icon={Receipt} label="Rechnungen" number="05" title="Deine Event-Rechnungen">
              {invoices.length === 0 ? (
                <p className="text-[14px] text-white/45" data-testid="no-invoices">Noch keine Rechnungen. Bei einer kostenpflichtigen Event-Anmeldung erscheint hier automatisch dein Beleg.</p>
              ) : (
                <div className="space-y-2" data-testid="invoice-list">
                  {invoices.map((inv) => (
                    <div key={inv.invoice_no} className={`flex items-center justify-between gap-3 border border-white/10 bg-white/[0.02] p-3 ${inv.refunded ? "opacity-60" : ""}`} data-testid={`invoice-row-${inv.invoice_no}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-outfit font-semibold text-white tracking-tight">{inv.invoice_no}</span>
                          {inv.refunded && (
                            <span className="px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-outfit font-bold border border-amber-400/40 text-amber-300 bg-amber-400/10">Erstattet</span>
                          )}
                        </div>
                        <p className="text-[13px] text-white/55 truncate">{inv.event_title}</p>
                        <p className="text-[11px] text-white/35 mt-0.5">{new Date(inv.issued_at).toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" })} · {(inv.amount_total_eur || 0).toFixed(2).replace(".", ",")} €</p>
                      </div>
                      <a href={`${API}/payments/invoices/${inv.invoice_no}/pdf`} target="_blank" rel="noreferrer"
                        data-testid={`invoice-download-${inv.invoice_no}`}
                        className="inline-flex items-center gap-1.5 px-3 h-9 border border-[#00e5ff]/40 text-[#00e5ff] text-[12px] uppercase tracking-wider font-outfit font-semibold hover:bg-[#00e5ff]/10 transition-colors">
                        <Download size={13} /> PDF
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </div>

          <div className="mt-6">
            <TwoFAPanel />
          </div>
        </div>
      </div>
    </div>
  );
}
